import { useEffect, useState } from "react";
import axios from "axios";
import { Camera, Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

export const GrvPasteImageButton = ({ item, index, onUploaded }) => {
  const [open, setOpen] = useState(false);
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onPaste = async (e) => {
      const file = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/"))?.getAsFile();
      if (!file) return toast.error("No picture on the clipboard — copy an image first");
      e.preventDefault();
      setImage(await readAsDataUrl(file));
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [open]);

  const close = () => { setOpen(false); setImage(null); };

  const save = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("image_data", image);
      await axios.post(`${API}/product-images/upload/${item.product_id}`, fd);
      toast.success(`Picture saved for ${item.name}`);
      onUploaded(item.product_id);
      close();
    } catch (err) {
      toast.error(`Failed to save picture — ${err.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-0.5 h-5 px-1.5 text-[10px] font-medium rounded border border-dashed border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors"
        title="This product has no picture — click, then press Ctrl+V"
        data-testid={`grv-add-picture-${index}`}
      >
        <Camera className="h-3 w-3" /> Add picture
      </button>
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent className="max-w-md" data-testid="grv-paste-image-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Picture for {item.name}</DialogTitle>
          </DialogHeader>
          <div
            className="border-2 border-dashed rounded-lg h-64 flex items-center justify-center bg-gray-50 overflow-hidden"
            data-testid="grv-paste-image-dropzone"
          >
            {image ? (
              <img src={image} alt="Pasted preview" className="max-h-full max-w-full object-contain" data-testid="grv-paste-image-preview" />
            ) : (
              <div className="text-center text-gray-500 text-sm">
                <ImagePlus className="h-10 w-10 mx-auto mb-2 text-gray-400" />
                Copy a picture, then press <kbd className="px-1.5 py-0.5 bg-white border rounded font-mono text-xs">Ctrl + V</kbd>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={close} data-testid="grv-paste-image-cancel">Cancel</Button>
            <Button onClick={save} disabled={!image || saving} data-testid="grv-paste-image-save">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save picture
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
